<template>
  <div class="container mt-5 mb-5">
    <div class="row justify-content-center">
      <div class="col-lg-10">

        <h1 class="mb-1" style="font-family: var(--font-heading);">
          <i class="ra ra-book me-2"></i>AI Playtester
        </h1>
        <p class="text-muted mb-4">
          Get structured feedback on your rules before you sit down with real playtesters.
        </p>

        <!-- Mode Selection -->
        <div v-if="!activeMode" class="row row-cols-1 row-cols-md-3 g-4 mb-4">
          <div class="col">
            <div
              class="card h-100 mode-card active-mode"
              @click="selectMode('quick-read')"
              role="button"
            >
              <div class="card-body text-center">
                <div class="mode-icon mb-3">
                  <i class="ra ra-book"></i>
                </div>
                <h5 class="card-title" style="font-family: var(--font-heading);">Quick Read</h5>
                <p class="card-text text-muted small">
                  Rules clarity review — reads your rules as a first-time player and flags confusion, ambiguity, and missing info.
                </p>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="card h-100 mode-card disabled-mode">
              <div class="card-body text-center">
                <div class="mode-icon mb-3 text-muted">
                  <i class="ra ra-gear-hammer"></i>
                </div>
                <h5 class="card-title text-muted" style="font-family: var(--font-heading);">Mechanical Autopsy</h5>
                <p class="card-text text-muted small">
                  Systems & balance analysis. Coming soon.
                </p>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="card h-100 mode-card disabled-mode">
              <div class="card-body text-center">
                <div class="mode-icon mb-3 text-muted">
                  <i class="ra ra-player-teleport"></i>
                </div>
                <h5 class="card-title text-muted" style="font-family: var(--font-heading);">Player Simulation</h5>
                <p class="card-text text-muted small">
                  Simulated playthrough with 3 player archetypes. Coming soon.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Read Input -->
        <div v-if="activeMode === 'quick-read' && !report">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0" style="font-family: var(--font-heading);">
                <i class="ra ra-book me-2"></i>Quick Read — Rules Clarity Review
              </h5>
              <button class="btn btn-sm btn-outline-secondary" @click="activeMode = null">
                Back
              </button>
            </div>
            <div class="card-body">

              <!-- Linked game banner -->
              <div v-if="linkedGame" class="alert alert-info d-flex justify-content-between align-items-center mb-4">
                <div>
                  <strong>{{ linkedGame.name }}</strong>
                  <span v-if="linkedGame.rules" class="ms-2 badge bg-success">Rules loaded</span>
                  <span v-else class="ms-2 badge bg-warning text-dark">No rules found — paste or upload below</span>
                </div>
                <button class="btn btn-sm btn-outline-secondary" @click="linkedGame = null; selectedGameId = ''; rulesText = ''">
                  Change Game
                </button>
              </div>

              <!-- Game selector -->
              <div v-if="!linkedGame" class="mb-4">
                <label class="form-label fw-bold">Select a game</label>
                <div v-if="loadingGame" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                  Loading game...
                </div>
                <select v-else v-model="selectedGameId" class="form-select" @change="onGameSelected">
                  <option value="">— Choose a game —</option>
                  <option v-for="game in userGames" :key="game.id" :value="game.id">
                    {{ game.name }} {{ game.rules ? '(has rules)' : '(no rules)' }}
                  </option>
                </select>
                <small class="text-muted">Or paste/upload rules below without linking a game.</small>
              </div>

              <!-- Input method tabs -->
              <ul class="nav nav-tabs mb-3">
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: inputMethod === 'paste' }"
                    @click="inputMethod = 'paste'"
                  >
                    Paste Rules Text
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    class="nav-link"
                    :class="{ active: inputMethod === 'upload' }"
                    @click="inputMethod = 'upload'"
                  >
                    Upload File
                  </button>
                </li>
              </ul>

              <!-- Paste input -->
              <div v-if="inputMethod === 'paste'">
                <textarea
                  v-model="rulesText"
                  class="form-control mb-2"
                  rows="14"
                  placeholder="Paste your game rules here. Include setup instructions, turn structure, win conditions, and any special rules.

Example:
SETUP: Each player takes a hero card and 3 starting items...
ON YOUR TURN: Move up to 3 spaces, then take one action...
COMBAT: Roll 2d6 + your attack modifier..."
                ></textarea>
                <div class="d-flex justify-content-between align-items-center">
                  <small :class="charCountClass">
                    {{ rulesText.length.toLocaleString() }} / {{ maxChars.toLocaleString() }} characters
                    <span v-if="rulesText.length > 0 && rulesText.length < minChars" class="text-danger">
                      — need at least {{ minChars }} characters
                    </span>
                  </small>
                  <small class="text-muted">~{{ estimatedPages }} page{{ estimatedPages !== 1 ? 's' : '' }}</small>
                </div>
              </div>

              <!-- Upload input -->
              <div v-if="inputMethod === 'upload'">
                <div class="upload-zone" @drop.prevent="handleDrop" @dragover.prevent>
                  <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileUpload"
                    accept=".txt,.md,.pdf"
                    class="d-none"
                  >
                  <div v-if="!uploadedFile" class="text-center p-4" @click="$refs.fileInput.click()" role="button">
                    <i class="bi bi-cloud-arrow-up" style="font-size: 2rem; color: var(--pop-blue);"></i>
                    <p class="mt-2 mb-1">Drop a file here or click to browse</p>
                    <small class="text-muted">
                      .txt or .md files (max {{ maxFileSizeMB }}MB)
                    </small>
                  </div>
                  <div v-else class="text-center p-3">
                    <i class="bi bi-file-text" style="font-size: 1.5rem; color: var(--pop-green);"></i>
                    <p class="mt-1 mb-1">{{ uploadedFile.name }} ({{ formatFileSize(uploadedFile.size) }})</p>
                    <button class="btn btn-sm btn-outline-danger" @click="clearFile">Remove</button>
                  </div>
                </div>
                <div v-if="fileError" class="alert alert-danger mt-2 small">{{ fileError }}</div>
                <div v-if="rulesText && uploadedFile" class="mt-2">
                  <small :class="charCountClass">
                    {{ rulesText.length.toLocaleString() }} characters extracted
                  </small>
                </div>
              </div>

              <!-- Guardrails info -->
              <div class="alert alert-light mt-3 small">
                <i class="bi bi-info-circle me-1"></i>
                <strong>What makes a good submission:</strong> Include your complete setup instructions, turn structure,
                win/loss conditions, and any special rules. The more complete, the better the feedback.
                Leave out flavor text and lore — focus on the mechanical rules.
              </div>

              <!-- Submit -->
              <div class="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <span v-if="validationError" class="text-danger small">{{ validationError }}</span>
                </div>
                <button
                  class="btn btn-primary btn-lg"
                  :disabled="!canSubmit || processing"
                  @click="submitQuickRead"
                >
                  <span v-if="processing">
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Analyzing rules...
                  </span>
                  <span v-else>
                    <i class="ra ra-book me-2"></i>Run Quick Read
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Processing Stages -->
        <div v-if="processing" class="mt-4">
          <div class="card">
            <div class="card-body p-4">
              <h5 class="mb-4 text-center" style="font-family: var(--font-heading);">Analyzing your rules...</h5>
              <div class="analysis-stages">
                <div v-for="(stage, i) in analysisStages" :key="i" class="d-flex align-items-center mb-3">
                  <div class="stage-indicator me-3">
                    <div v-if="currentStage > i" class="stage-done">
                      <i class="bi bi-check-circle-fill text-success"></i>
                    </div>
                    <div v-else-if="currentStage === i" class="stage-active">
                      <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                    </div>
                    <div v-else class="stage-pending">
                      <i class="bi bi-circle text-muted"></i>
                    </div>
                  </div>
                  <div :class="{ 'text-muted': currentStage < i, 'fw-bold': currentStage === i }">
                    {{ stage }}
                  </div>
                </div>
              </div>
              <div class="text-center mt-3">
                <small class="text-muted">This usually takes 30-60 seconds</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Report -->
        <div v-if="report && !report.parseError">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 style="font-family: var(--font-heading);">
              <i class="ra ra-book me-2"></i>Quick Read Report
              <span v-if="linkedGame" class="h6 text-muted ms-2">— {{ linkedGame.name }}</span>
            </h3>
            <div>
              <button
                v-if="linkedGame && !savedToProject"
                class="btn btn-success btn-sm me-2"
                @click="saveReportToProject"
                :disabled="savingToProject"
              >
                <span v-if="savingToProject" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="bi bi-bookmark-plus me-1"></i>Save to Project
              </button>
              <span v-if="savedToProject" class="badge bg-success me-2">
                <i class="bi bi-check me-1"></i>Saved
              </span>
              <button class="btn btn-outline-secondary btn-sm me-2" @click="resetReview">
                New Review
              </button>
              <button class="btn btn-outline-primary btn-sm" @click="printReport">
                <i class="bi bi-printer me-1"></i>Print
              </button>
            </div>
          </div>

          <!-- First Impression -->
          <div class="card mb-3 report-section">
            <div class="card-header">
              <h5 class="mb-0">First Impression</h5>
            </div>
            <div class="card-body">
              <p>{{ report.firstImpression }}</p>
            </div>
          </div>

          <!-- Strengths -->
          <div v-if="report.strengths && report.strengths.length" class="card mb-3 report-section">
            <div class="card-header bg-success bg-opacity-10">
              <h5 class="mb-0 text-success"><i class="bi bi-check-circle me-2"></i>What's Working</h5>
            </div>
            <div class="card-body">
              <ul class="mb-0">
                <li v-for="(s, i) in report.strengths" :key="i">{{ s }}</li>
              </ul>
            </div>
          </div>

          <!-- Ambiguities -->
          <div v-if="report.ambiguities && report.ambiguities.length" class="card mb-3 report-section">
            <div class="card-header bg-warning bg-opacity-10">
              <h5 class="mb-0 text-warning"><i class="bi bi-exclamation-triangle me-2"></i>Ambiguities</h5>
            </div>
            <div class="card-body">
              <div v-for="(a, i) in report.ambiguities" :key="i" class="mb-3" :class="{ 'border-bottom pb-3': i < report.ambiguities.length - 1 }">
                <blockquote class="blockquote small bg-light p-2 rounded">
                  "{{ a.quote }}"
                </blockquote>
                <p class="mb-1"><strong>Issue:</strong> {{ a.issue }}</p>
                <p class="mb-0 text-muted"><strong>Suggestion:</strong> {{ a.suggestion }}</p>
              </div>
            </div>
          </div>

          <!-- Missing Rules -->
          <div v-if="report.missingRules && report.missingRules.length" class="card mb-3 report-section">
            <div class="card-header bg-danger bg-opacity-10">
              <h5 class="mb-0 text-danger"><i class="bi bi-question-circle me-2"></i>Missing Rules</h5>
            </div>
            <div class="card-body">
              <div v-for="(m, i) in report.missingRules" :key="i" class="mb-2">
                <p class="mb-0"><strong>{{ m.situation }}</strong></p>
                <p class="mb-0 text-muted small">{{ m.why }}</p>
              </div>
            </div>
          </div>

          <!-- Setup Gaps -->
          <div v-if="report.setupGaps && report.setupGaps.length" class="card mb-3 report-section">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-layout-text-sidebar me-2"></i>Setup Gaps</h5>
            </div>
            <div class="card-body">
              <ul class="mb-0">
                <li v-for="(g, i) in report.setupGaps" :key="i">{{ g }}</li>
              </ul>
            </div>
          </div>

          <!-- Terminology Issues -->
          <div v-if="report.terminologyIssues && report.terminologyIssues.length" class="card mb-3 report-section">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-chat-quote me-2"></i>Terminology Issues</h5>
            </div>
            <div class="card-body">
              <div v-for="(t, i) in report.terminologyIssues" :key="i" class="mb-2">
                <span class="badge bg-secondary me-2">{{ t.term }}</span>
                <span>{{ t.issue }}</span>
              </div>
            </div>
          </div>

          <!-- Suggested Rewrites -->
          <div v-if="report.suggestedRewrites && report.suggestedRewrites.length" class="card mb-3 report-section">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-pencil me-2"></i>Suggested Rewrites</h5>
            </div>
            <div class="card-body">
              <div v-for="(r, i) in report.suggestedRewrites" :key="i" class="mb-3" :class="{ 'border-bottom pb-3': i < report.suggestedRewrites.length - 1 }">
                <span class="badge mb-2" :class="severityBadge(r.severity)">{{ r.severity }}</span>
                <div class="row">
                  <div class="col-md-6">
                    <small class="text-muted d-block mb-1">Before:</small>
                    <div class="bg-light p-2 rounded small text-decoration-line-through">{{ r.original }}</div>
                  </div>
                  <div class="col-md-6">
                    <small class="text-muted d-block mb-1">After:</small>
                    <div class="bg-success bg-opacity-10 p-2 rounded small">{{ r.rewrite }}</div>
                  </div>
                </div>
                <small class="text-muted mt-1 d-block">{{ r.reason }}</small>
              </div>
            </div>
          </div>

          <!-- Playtest Questions -->
          <div class="card mb-3 report-section">
            <div class="card-header bg-primary bg-opacity-10">
              <h5 class="mb-0 text-primary">
                <i class="bi bi-people me-2"></i>Questions for Real Playtesters
              </h5>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">
                You've gotten what AI can tell you from the rules alone. Use these questions when you test with real people:
              </p>
              <ol>
                <li v-for="(q, i) in report.playtestQuestions" :key="i" class="mb-2">{{ q }}</li>
              </ol>
            </div>
          </div>

          <!-- Next Steps -->
          <div class="card border-dashed">
            <div class="card-body text-center">
              <h5 style="font-family: var(--font-heading);">Ready for real feedback?</h5>
              <p class="text-muted mb-3">
                AI can catch unclear rules, but only real players can tell you if the game is <em>fun</em>.
              </p>
              <div class="row row-cols-1 row-cols-md-2 g-3 text-start">
                <div class="col">
                  <div class="d-flex align-items-start">
                    <i class="bi bi-book me-2 mt-1 text-primary"></i>
                    <div>
                      <strong>The Board Game Designer's Guide</strong>
                      <br><a href="https://www.adamportergames.com/" target="_blank" class="small">adamportergames.com</a>
                    </div>
                  </div>
                </div>
                <div class="col">
                  <div class="d-flex align-items-start">
                    <i class="bi bi-people me-2 mt-1 text-primary"></i>
                    <div>
                      <strong>Find playtest groups</strong>
                      <br><span class="small text-muted">BoardGameGeek forums, r/tabletopgamedesign, local Meetups</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Parse Error Fallback — render as formatted markdown -->
        <div v-if="report && report.parseError">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h3 style="font-family: var(--font-heading);">
              <i class="ra ra-book me-2"></i>Quick Read Report
              <span v-if="linkedGame" class="h6 text-muted ms-2">— {{ linkedGame.name }}</span>
            </h3>
            <div>
              <button
                v-if="linkedGame && !savedToProject"
                class="btn btn-success btn-sm me-2"
                @click="saveReportToProject"
                :disabled="savingToProject"
              >
                <span v-if="savingToProject" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="bi bi-bookmark-plus me-1"></i>Save to Project
              </button>
              <span v-if="savedToProject" class="badge bg-success me-2">
                <i class="bi bi-check me-1"></i>Saved
              </span>
              <button class="btn btn-outline-secondary btn-sm me-2" @click="resetReview">
                New Review
              </button>
              <button class="btn btn-outline-primary btn-sm" @click="printReport">
                <i class="bi bi-printer me-1"></i>Print
              </button>
            </div>
          </div>
          <div class="card report-section">
            <div class="card-body">
              <div v-html="formattedRawReport" class="raw-report-content"></div>
            </div>
          </div>

          <!-- Still show next steps -->
          <div class="card border-dashed mt-3">
            <div class="card-body text-center">
              <h5 style="font-family: var(--font-heading);">Ready for real feedback?</h5>
              <p class="text-muted mb-3">
                AI can catch unclear rules, but only real players can tell you if the game is <em>fun</em>.
              </p>
              <div class="row row-cols-1 row-cols-md-2 g-3 text-start">
                <div class="col">
                  <div class="d-flex align-items-start">
                    <i class="bi bi-book me-2 mt-1 text-primary"></i>
                    <div>
                      <strong>The Board Game Designer's Guide</strong>
                      <br><a href="https://www.adamportergames.com/" target="_blank" class="small">adamportergames.com</a>
                    </div>
                  </div>
                </div>
                <div class="col">
                  <div class="d-flex align-items-start">
                    <i class="bi bi-people me-2 mt-1 text-primary"></i>
                    <div>
                      <strong>Find playtest groups</strong>
                      <br><span class="small text-muted">BoardGameGeek forums, r/tabletopgamedesign, local Meetups</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error -->
        <div v-if="error" class="alert alert-danger mt-3">
          <strong>Error:</strong> {{ error }}
          <button class="btn btn-sm btn-outline-danger ms-3" @click="error = null">Dismiss</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { firestore, runQuickRead, savePlaytestReview, QUICK_READ_LIMITS } from '@/firebase'
import { marked } from 'marked'

export default {
  name: 'PlaytestReviewPage',
  data() {
    return {
      user: null,
      activeMode: null,
      inputMethod: 'paste',
      rulesText: '',
      uploadedFile: null,
      fileError: null,
      selectedGameId: '',
      userGames: [],
      processing: false,
      report: null,
      error: null,
      validationError: null,
      maxChars: QUICK_READ_LIMITS.maxChars,
      minChars: QUICK_READ_LIMITS.minChars,
      maxFileSizeMB: QUICK_READ_LIMITS.maxFileSizeMB,
      linkedGame: null,
      loadingGame: false,
      currentStage: 0,
      stageTimer: null,
      analysisStages: [
        'Reading rules as a first-time player...',
        'Checking for ambiguities and missing rules...',
        'Auditing terminology and setup instructions...',
        'Identifying strengths and writing suggestions...',
        'Generating playtest questions...'
      ],
      savedToProject: false,
      savingToProject: false,
      savedReviewId: null
    }
  },
  computed: {
    estimatedPages() {
      return Math.max(1, Math.round(this.rulesText.length / 2000))
    },
    charCountClass() {
      if (this.rulesText.length > this.maxChars) return 'text-danger'
      if (this.rulesText.length > this.maxChars * 0.9) return 'text-warning'
      return 'text-muted'
    },
    canSubmit() {
      return this.rulesText.length >= this.minChars && this.rulesText.length <= this.maxChars
    },
    formattedRawReport() {
      if (!this.report || !this.report.rawResponse) return ''
      // Strip markdown code fences if present, then render as markdown
      let text = this.report.rawResponse
      text = text.replace(/```json\s*/g, '').replace(/```\s*/g, '')
      // Try to detect if it's JSON and format it nicely
      try {
        const parsed = JSON.parse(text)
        // If it parsed as JSON, convert to readable markdown
        return marked.parse(this.jsonToMarkdown(parsed))
      } catch {
        // Not JSON — render as markdown directly
        return marked.parse(text)
      }
    }
  },
  methods: {
    selectMode(mode) {
      this.activeMode = mode
    },

    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) this.processFile(file)
    },

    handleDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file) this.processFile(file)
    },

    processFile(file) {
      this.fileError = null
      this.uploadedFile = null

      if (file.size > this.maxFileSizeMB * 1024 * 1024) {
        this.fileError = `File is too large (${this.formatFileSize(file.size)}). Maximum is ${this.maxFileSizeMB}MB.`
        return
      }

      const ext = file.name.split('.').pop().toLowerCase()
      if (!['txt', 'md'].includes(ext)) {
        this.fileError = 'Only .txt and .md files are supported for now. PDF support coming soon — paste your rules text instead.'
        return
      }

      this.uploadedFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.rulesText = e.target.result
      }
      reader.onerror = () => {
        this.fileError = 'Failed to read file.'
      }
      reader.readAsText(file)
    },

    clearFile() {
      this.uploadedFile = null
      this.rulesText = ''
      this.fileError = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    severityBadge(severity) {
      const map = { critical: 'bg-danger', important: 'bg-warning text-dark', minor: 'bg-secondary' }
      return map[severity] || 'bg-secondary'
    },

    startStageTimer() {
      this.currentStage = 0
      this.stageTimer = setInterval(() => {
        if (this.currentStage < this.analysisStages.length - 1) {
          this.currentStage++
        }
      }, 8000)
    },

    stopStageTimer() {
      if (this.stageTimer) {
        clearInterval(this.stageTimer)
        this.stageTimer = null
      }
      this.currentStage = this.analysisStages.length
    },

    jsonToMarkdown(obj) {
      let md = ''
      if (obj.firstImpression) {
        md += `## First Impression\n\n${obj.firstImpression}\n\n`
      }
      if (obj.strengths && obj.strengths.length) {
        md += `## What's Working\n\n${obj.strengths.map(s => `- ${s}`).join('\n')}\n\n`
      }
      if (obj.ambiguities && obj.ambiguities.length) {
        md += `## Ambiguities\n\n`
        obj.ambiguities.forEach(a => {
          md += `> "${a.quote}"\n\n**Issue:** ${a.issue}\n\n**Suggestion:** ${a.suggestion}\n\n---\n\n`
        })
      }
      if (obj.missingRules && obj.missingRules.length) {
        md += `## Missing Rules\n\n`
        obj.missingRules.forEach(m => {
          md += `- **${m.situation}** — ${m.why}\n`
        })
        md += '\n'
      }
      if (obj.setupGaps && obj.setupGaps.length) {
        md += `## Setup Gaps\n\n${obj.setupGaps.map(g => `- ${g}`).join('\n')}\n\n`
      }
      if (obj.terminologyIssues && obj.terminologyIssues.length) {
        md += `## Terminology Issues\n\n`
        obj.terminologyIssues.forEach(t => {
          md += `- **${t.term}** — ${t.issue}\n`
        })
        md += '\n'
      }
      if (obj.suggestedRewrites && obj.suggestedRewrites.length) {
        md += `## Suggested Rewrites\n\n`
        obj.suggestedRewrites.forEach(r => {
          md += `### ${(r.severity || 'suggestion').toUpperCase()}\n\n`
          md += `**Before:** ${r.original}\n\n**After:** ${r.rewrite}\n\n*${r.reason}*\n\n---\n\n`
        })
      }
      if (obj.playtestQuestions && obj.playtestQuestions.length) {
        md += `## Questions for Real Playtesters\n\n`
        obj.playtestQuestions.forEach((q, i) => {
          md += `${i + 1}. ${q}\n`
        })
        md += '\n'
      }
      return md
    },

    async saveReportToProject() {
      if (!this.user || !this.linkedGame) return
      this.savingToProject = true
      try {
        const reviewId = await savePlaytestReview(
          this.user.uid,
          this.linkedGame.id,
          'quick-read',
          { description: this.linkedGame.name, rulesText: this.rulesText, fromApp: true },
          this.report
        )
        this.savedReviewId = reviewId
        this.savedToProject = true
      } catch (err) {
        console.error('Error saving to project:', err)
        this.error = 'Failed to save review to project.'
      } finally {
        this.savingToProject = false
      }
    },

    async submitQuickRead() {
      this.validationError = null
      this.error = null
      this.savedToProject = false

      if (!this.canSubmit) {
        this.validationError = 'Check the character count before submitting.'
        return
      }

      this.processing = true
      this.startStageTimer()

      try {
        const gameContext = {}
        if (this.selectedGameId) {
          const game = this.userGames.find(g => g.id === this.selectedGameId) || this.linkedGame
          if (game) {
            gameContext.name = game.name
            gameContext.type = game.type
          }
        }

        const result = await runQuickRead(this.rulesText, gameContext)
        this.stopStageTimer()
        this.report = result

        // Auto-save if linked to a game
        if (this.user && this.linkedGame && !result.parseError) {
          await this.saveReportToProject()
        }
      } catch (err) {
        console.error('Quick Read error:', err)
        this.error = err.message || 'Something went wrong. Please try again.'
      } finally {
        this.stopStageTimer()
        this.processing = false
      }
    },

    resetReview() {
      this.report = null
      this.rulesText = ''
      this.uploadedFile = null
      this.selectedGameId = ''
      this.error = null
      this.activeMode = null
    },

    printReport() {
      window.print()
    },

    async fetchUserGames() {
      if (!this.user) return
      try {
        const gamesRef = collection(firestore, 'boardGames')
        const q = query(gamesRef, where('userId', '==', this.user.uid))
        const snapshot = await getDocs(q)
        this.userGames = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (err) {
        console.error('Error fetching user games:', err)
      }
    },

    async loadGameRules(gameId) {
      this.loadingGame = true
      try {
        const gameRef = doc(firestore, 'boardGames', gameId)
        const gameSnap = await getDoc(gameRef)
        if (gameSnap.exists()) {
          const gameData = { id: gameSnap.id, ...gameSnap.data() }
          this.linkedGame = gameData
          this.selectedGameId = gameId
          if (gameData.rules && gameData.rules.length > 0) {
            this.rulesText = gameData.rules
          }
        }
      } catch (err) {
        console.error('Error loading game:', err)
        this.error = 'Failed to load game rules.'
      } finally {
        this.loadingGame = false
      }
    },

    onGameSelected() {
      if (this.selectedGameId) {
        const game = this.userGames.find(g => g.id === this.selectedGameId)
        if (game) {
          this.linkedGame = game
          if (game.rules && game.rules.length > 0) {
            this.rulesText = game.rules
          } else {
            this.rulesText = ''
          }
        }
      } else {
        this.linkedGame = null
        this.rulesText = ''
      }
    }
  },
  created() {
    const auth = getAuth()
    onAuthStateChanged(auth, async (user) => {
      this.user = user
      if (user) {
        await this.fetchUserGames()
        // Auto-load game if passed via query (after games list is ready)
        if (this.$route.query.gameId) {
          this.selectedGameId = this.$route.query.gameId
          await this.loadGameRules(this.$route.query.gameId)
        }
      }
    })

    // Pre-select mode if passed via query
    if (this.$route.query.mode === 'quick-read') {
      this.activeMode = 'quick-read'
    }
  }
}
</script>

<style scoped>
.mode-card {
  border-radius: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 2px solid var(--border-sketch, rgba(0,0,0,0.08));
}

.active-mode {
  cursor: pointer;
}

.active-mode:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px var(--shadow-med, rgba(45,45,45,0.12));
  border-color: var(--pop-orange, #ff6b35);
}

.disabled-mode {
  opacity: 0.5;
  cursor: not-allowed;
}

.mode-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--bg-elevated, #fff8f0);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: var(--pop-orange, #ff6b35);
}

.upload-zone {
  border: 2px dashed var(--pencil-light, #d4c9b5);
  border-radius: 12px;
  background: var(--bg-elevated, #fff8f0);
  transition: border-color 0.2s;
}

.upload-zone:hover {
  border-color: var(--pop-blue, #4a90d9);
}

.report-section {
  border-radius: 12px;
}

.border-dashed {
  border: 2px dashed var(--pencil-light, #d4c9b5);
  border-radius: 16px;
  background: var(--bg-elevated, #fff8f0);
}

.analysis-stages {
  max-width: 400px;
  margin: 0 auto;
}

.stage-indicator {
  width: 24px;
  text-align: center;
  font-size: 1.1rem;
}

.stage-active {
  animation: pulse-fade 1.5s ease-in-out infinite;
}

@keyframes pulse-fade {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.raw-report-content {
  line-height: 1.7;
}

.raw-report-content h2 {
  font-family: var(--font-heading);
  font-size: 1.3rem;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid var(--border-sketch, rgba(0,0,0,0.08));
}

.raw-report-content h3 {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 1rem;
}

.raw-report-content blockquote {
  background: var(--bg-elevated, #fff8f0);
  border-left: 3px solid var(--pop-orange, #ff6b35);
  padding: 0.5rem 1rem;
  margin: 0.5rem 0;
  border-radius: 4px;
  font-style: italic;
}

.raw-report-content hr {
  border: none;
  border-top: 1px solid var(--border-sketch, rgba(0,0,0,0.08));
  margin: 1rem 0;
}

.raw-report-content ol {
  padding-left: 1.5rem;
}

.raw-report-content li {
  margin-bottom: 0.4rem;
}

@media print {
  .btn, .nav-tabs, .alert-light, .border-dashed { display: none !important; }
}
</style>
